import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { ROLES, ROLE_LABEL } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Eye, EyeOff, KeyRound, Pencil, RotateCcw, Search, Trash2, WandSparkles,
} from "lucide-react";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const PAGE_SIZE = 10;

export default function UsersAdminPage() {
  const { user } = useAuth();

  const manageableRoles =
    user?.role === ROLES.SUPER_ADMIN
      ? Object.values(ROLES).filter(
          (role) => role !== ROLES.APPROVER
        )
      : user?.role === ROLES.SUPPORT_ADMIN
        ? [ROLES.SUPPORT_ADMIN, ROLES.SUPPORT_ENGINEER]
        : user?.role === ROLES.BUSINESS_ADMIN
          ? [
              ROLES.CLIENT_ADMIN,
              ROLES.BUSINESS_USER,
              ROLES.VIEWER,
            ]
          : user?.role === ROLES.CLIENT_ADMIN
            ? [ROLES.CLIENT_USER]
            : [];

  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);

  const organizationNameById = Object.fromEntries(
    organizations.map((organization) => [
      organization.id,
      organization.name,
    ]),
  );
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [resetForm, setResetForm] = useState({
    password: "",
    confirmPassword: "",
  });
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const emptyUserForm = {
    name: "",
    email: "",
    role: ROLES.CLIENT_USER,
    password: "",
    phone: "",
    organization_id: "",
  };

  const [form, setForm] = useState(emptyUserForm);
  const [editForm, setEditForm] = useState({ name: "", role: "", phone: "", employee_code: "", status: "active" });

  const load = () => {
    const params = { page, page_size: PAGE_SIZE, include_deleted: includeDeleted };
    if (q) params.q = q;
    if (role) params.role = role;
    api.get("/users2", { params }).then((r) => {
      setUsers(r.data.items || []);
      setTotal(r.data.total || 0);
    });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, q, role, includeDeleted]);

  useEffect(() => {
    const canChooseClient = [
      ROLES.SUPER_ADMIN,
      ROLES.BUSINESS_ADMIN,
      ROLES.CLIENT_ADMIN,
    ].includes(user?.role);

    if (!canChooseClient) {
      setOrganizations([]);
      return;
    }

    api.get("/organizations")
      .then((r) => setOrganizations(r.data || []))
      .catch((e) => toast.error(formatApiError(e)));
  }, [user?.role]);

  const create = async () => {
    const name = form.name.trim();
    const email = form.email.trim();
    const phone = form.phone.trim();
    const password = form.password;

    if (!name) {
      return toast.error("Name is required");
    }

    if (!email) {
      return toast.error("Email is required");
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return toast.error("Enter a valid email address");
    }

    if (!password) {
      return toast.error("Password is required");
    }

    if (password.length < 8) {
      return toast.error("Password must contain at least 8 characters");
    }

    if (password.length > 16) {
      return toast.error("Password must not exceed 16 characters");
    }

    if (!phone) {
      return toast.error("Phone number is required");
    }

    if (!form.role) {
      return toast.error("Role is required");
    }

    const clientRoleSelected = [
      ROLES.CLIENT_ADMIN,
      ROLES.CLIENT_USER,
    ].includes(form.role);

    if (
      clientRoleSelected &&
      [ROLES.SUPER_ADMIN, ROLES.BUSINESS_ADMIN].includes(user?.role) &&
      !form.organization_id
    ) {
      return toast.error("Client is required");
    }
    try {
      await api.post("/users", {
        ...form,
        name,
        email,
        phone,
      });
      toast.success("User created");
      setAddOpen(false);
      setForm(emptyUserForm);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const closeAddUser = () => {
    setAddOpen(false);
    setForm(emptyUserForm);
  };

  const startEdit = (u) => {
    setEditing(u);
    setEditForm({
      name: u.name || "",
      role: u.role,
      phone: u.phone || "",
      employee_code: u.employee_code || "",
      status: u.status || "active",
    });
  };
  const saveEdit = async () => {
    const name = editForm.name.trim();
    if (!name) {
      return toast.error("Name is required");
    }
    try {
      await api.patch(`/users/${editing.id}`, { ...editForm, name });
      toast.success("User updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleStatus = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { status: u.status === "active" ? "inactive" : "active" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const openResetPassword = (u) => {
    setResetUser(u);
    setResetForm({
      password: "",
      confirmPassword: "",
    });
    setShowResetPassword(false);
  };

  const closeResetPassword = () => {
    if (resettingPassword) return;
    setResetUser(null);
    setResetForm({
      password: "",
      confirmPassword: "",
    });
    setShowResetPassword(false);
  };

  const generateStrongPassword = () => {
    const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
    const lower = "abcdefghijkmnopqrstuvwxyz";
    const numbers = "23456789";
    const symbols = "!@#$%&*?";
    const all = upper + lower + numbers + symbols;

    const required = [
      upper[Math.floor(Math.random() * upper.length)],
      lower[Math.floor(Math.random() * lower.length)],
      numbers[Math.floor(Math.random() * numbers.length)],
      symbols[Math.floor(Math.random() * symbols.length)],
    ];

    while (required.length < 14) {
      required.push(all[Math.floor(Math.random() * all.length)]);
    }

    const password = required
      .sort(() => Math.random() - 0.5)
      .join("");

    setResetForm({
      password,
      confirmPassword: password,
    });
    setShowResetPassword(true);
  };

  const getPasswordStrength = (password) => {
    if (!password) return { label: "Enter a password", score: 0 };

    let score = 0;
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 1;
    if (/\d/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;

    if (score <= 2) return { label: "Weak", score: 33 };
    if (score <= 4) return { label: "Medium", score: 66 };
    return { label: "Strong", score: 100 };
  };

  const resetPw = async () => {
    const password = resetForm.password;
    const confirmPassword = resetForm.confirmPassword;

    if (!password || !confirmPassword) {
      return toast.error("Enter and confirm the new password");
    }

    if (password.length < 8) {
      return toast.error("Password must contain at least 8 characters");
    }

    if (password.length > 16) {
      return toast.error("Password must not exceed 16 characters");
    }

    if (confirmPassword.length > 16) {
      return toast.error("Confirm password must not exceed 16 characters");
    }

    if (password !== confirmPassword) {
      return toast.error("Passwords do not match");
    }

    try {
      setResettingPassword(true);

      await api.post(
        `/users/${resetUser.id}/reset-password`,
        { password },
      );

      toast.success("Password updated successfully");
      setResetUser(null);
      setResetForm({
        password: "",
        confirmPassword: "",
      });
      setShowResetPassword(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setResettingPassword(false);
    }
  };
  const doDelete = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      setConfirmDelete(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); setConfirmDelete(null); }
  };
  const doRestore = async (id) => {
    try {
      await api.post(`/users/${id}/restore`);
      toast.success("User restored successfully");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-4" data-testid="admin-users-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Administration</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Users &amp; roles</h1>
        </div>
        <Button data-testid="user-add" onClick={() => setAddOpen(true)} className="bg-[#d4af37] text-black hover:bg-[#e5c158]">Add user</Button>
      </div>

      <div className="flex flex-wrap gap-3 items-center rounded-xl border border-border bg-card/60 p-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="users-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search by name, email…" className="pl-9" />
        </div>
        <Select value={role} onValueChange={(v) => { setRole(v === "__all__" ? "" : v); setPage(1); }}>
          <SelectTrigger data-testid="users-role-filter" className="w-52"><SelectValue placeholder="All roles" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All roles</SelectItem>
            {manageableRoles.map((r) => (
              <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">Show deleted</span>
          <Switch data-testid="users-show-deleted" checked={includeDeleted} onCheckedChange={setIncludeDeleted} />
        </div>
        <div className="text-xs text-muted-foreground">{total} users</div>
      </div>

      <div className="rounded-xl border border-border bg-card/70 overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="text-[11px] uppercase tracking-widest text-muted-foreground">
            <tr className="border-b border-border">
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Client</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-row data-testid={`user-row-${u.email}`} className={`border-b border-border/70 ${u.deleted ? "opacity-60" : ""}`}>
                <td className="px-4 py-3">{u.name}</td>
                <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                <td className="px-4 py-3 text-xs">{ROLE_LABEL[u.role]}</td>
                <td className="px-4 py-3 text-xs">
                  {u.organization_id
                    ? organizationNameById[u.organization_id] || "Unknown client"
                    : "Miledeep"}
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs rounded-full px-2 py-0.5 border ${u.deleted ? "text-rose-300 border-rose-500/30" : u.status === "active" ? "text-emerald-300 border-emerald-500/30 bg-emerald-500/10" : "text-slate-300 border-slate-500/30"}`}>
                    {u.deleted ? "deleted" : u.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <TooltipProvider delayDuration={150}>
                    {u.deleted ? (
                      <div className="inline-flex justify-end">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => doRestore(u.id)}
                              data-testid={`user-restore-${u.email}`}
                              aria-label={`Restore ${u.name || u.email}`}
                              className="h-8 w-8"
                            >
                              <RotateCcw className="h-3.5 w-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            Restore user
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    ) : (
                      <div className="inline-flex items-center justify-end gap-1.5">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => startEdit(u)}
                              data-testid={`user-edit-${u.email}`}
                              aria-label={`Edit ${u.name || u.email}`}
                              className="h-8 w-8"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            Edit user
                          </TooltipContent>
                        </Tooltip>

                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => toggleStatus(u)}
                              data-testid={`user-toggle-${u.email}`}
                              aria-label={
                                u.status === "active"
                                  ? `Deactivate ${u.name || u.email}`
                                  : `Activate ${u.name || u.email}`
                              }
                              className="h-8 w-8"
                            >
                              <span
                                className={`h-2.5 w-2.5 rounded-full ${
                                  u.status === "active"
                                    ? "bg-emerald-400"
                                    : "bg-slate-400"
                                }`}
                              />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            {u.status === "active"
                              ? "Deactivate user"
                              : "Activate user"}
                          </TooltipContent>
                        </Tooltip>

                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => openResetPassword(u)}
                              data-testid={`user-reset-${u.email}`}
                              aria-label={`Reset password for ${u.name || u.email}`}
                              className="h-8 w-8"
                            >
                              <KeyRound className="h-3.5 w-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            Reset password
                          </TooltipContent>
                        </Tooltip>

                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => setConfirmDelete(u)}
                              data-testid={`user-delete-${u.email}`}
                              aria-label={`Delete ${u.name || u.email}`}
                              className="h-8 w-8 border-rose-500/30 text-rose-400 hover:bg-rose-500/10 hover:text-rose-300"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            Delete user
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    )}
                  </TooltipProvider>
                </td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-xs text-muted-foreground">No users match your filters.</td></tr>}
          </tbody>
        </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-border text-xs">
          <div className="text-muted-foreground">Page {page} of {totalPages}</div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="users-prev">Prev</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="users-next">Next</Button>
          </div>
        </div>
      </div>

      {/* Add dialog */}
      <Dialog
        open={addOpen}
        onOpenChange={(open) => {
          setAddOpen(open);
          if (!open) setForm(emptyUserForm);
        }}
      >
        <DialogContent data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle>Add a new user</DialogTitle>
            <DialogDescription>Create an account with a specific role and password.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label>Name<span className="ml-1 text-rose-500">*</span></Label><Input data-testid="new-user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Email<span className="ml-1 text-rose-500">*</span></Label><Input data-testid="new-user-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Password<span className="ml-1 text-rose-500">*</span></Label><Input data-testid="new-user-password" type="password" maxLength={16} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            <div>
              <Label>
                Phone Number
                <span className="ml-1 text-rose-500">*</span>
              </Label>
              <Input
                data-testid="new-user-phone"
                value={form.phone}
                onChange={(e) =>
                  setForm({
                    ...form,
                    phone: e.target.value,
                  })
                }
                placeholder="Enter phone number"
                required
                aria-required="true"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Role<span className="ml-1 text-rose-500">*</span></Label>
              <Select
                value={form.role}
                onValueChange={(v) =>
                  setForm({
                    ...form,
                    role: v,
                    organization_id: [
                      ROLES.CLIENT_ADMIN,
                      ROLES.CLIENT_USER,
                    ].includes(v)
                      ? form.organization_id
                      : "",
                  })
                }
              >
                <SelectTrigger data-testid="new-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {manageableRoles.map((r) => (
                    <SelectItem key={r} value={r}>
                      {ROLE_LABEL[r]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {[
              ROLES.CLIENT_ADMIN,
              ROLES.CLIENT_USER,
            ].includes(form.role) &&
              [ROLES.SUPER_ADMIN, ROLES.BUSINESS_ADMIN].includes(user?.role) && (
              <div className="md:col-span-2">
                <Label>Client<span className="ml-1 text-rose-500">*</span></Label>
                <Select
                  value={form.organization_id}
                  onValueChange={(v) =>
                    setForm({ ...form, organization_id: v })
                  }
                >
                  <SelectTrigger data-testid="new-user-organization">
                    <SelectValue placeholder="Select client organization" />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations.map((organization) => (
                      <SelectItem
                        key={organization.id}
                        value={organization.id}
                      >
                        {organization.name}
                        {organization.code
                          ? ` (${organization.code})`
                          : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {organizations.length === 0 && (
                  <div className="mt-1 text-xs text-muted-foreground">
                    No client organizations are available.
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeAddUser}>Cancel</Button>
            <Button data-testid="new-user-create" onClick={create} className="bg-[#d4af37] text-black hover:bg-[#e5c158]">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Edit user</DialogTitle>
            {editing && <DialogDescription>{editing.email}</DialogDescription>}
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label>Name<span className="ml-1 text-rose-500">*</span></Label><Input data-testid="edit-user-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></div>
            <div><Label>Phone</Label><Input data-testid="edit-user-phone" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} /></div>
            <div><Label>Employee code</Label><Input data-testid="edit-user-code" value={editForm.employee_code} onChange={(e) => setEditForm({ ...editForm, employee_code: e.target.value })} /></div>
            <div><Label>Role</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {manageableRoles.map((r) => (
              <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>
            ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Status</Label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                <SelectTrigger data-testid="edit-user-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button data-testid="edit-user-save" onClick={saveEdit} className="bg-[#d4af37] text-black hover:bg-[#e5c158]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog
        open={!!resetUser}
        onOpenChange={(open) => {
          if (!open) closeResetPassword();
        }}
      >
        <DialogContent data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle>Reset password</DialogTitle>
            <DialogDescription>
              Create a new password for{" "}
              <span className="font-medium text-foreground">
                {resetUser?.name || resetUser?.email}
              </span>
              {resetUser?.email && (
                <span className="block mt-1 text-xs">
                  {resetUser.email}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="reset-user-password">New password</Label>

                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={generateStrongPassword}
                  className="h-8 px-2 text-xs"
                >
                  <WandSparkles className="mr-1.5 h-3.5 w-3.5" />
                  Generate
                </Button>
              </div>

              <div className="relative">
                <Input
                  id="reset-user-password"
                  data-testid="reset-user-password"
                  type={showResetPassword ? "text" : "password"}
                  maxLength={16}
                    value={resetForm.password}
                  autoComplete="new-password"
                  placeholder="Enter 8 to 16 characters"
                  className="pr-10"
                  onChange={(e) =>
                    setResetForm({
                      ...resetForm,
                      password: e.target.value,
                    })
                  }
                />

                <button
                  type="button"
                  onClick={() => setShowResetPassword((current) => !current)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showResetPassword ? "Hide password" : "Show password"}
                >
                  {showResetPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>

              {(() => {
                const strength = getPasswordStrength(resetForm.password);

                return (
                  <div className="space-y-1.5">
                    <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-[#d4af37] transition-all"
                        style={{ width: `${strength.score}%` }}
                      />
                    </div>

                    <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                      <span>Password strength: {strength.label}</span>
                      <span>8–16 characters</span>
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="space-y-2">
              <Label htmlFor="reset-user-confirm-password">
                Confirm password
              </Label>

              <Input
                id="reset-user-confirm-password"
                data-testid="reset-user-confirm-password"
                type={showResetPassword ? "text" : "password"}
                maxLength={16}
                    value={resetForm.confirmPassword}
                autoComplete="new-password"
                placeholder="Re-enter the new password"
                onChange={(e) =>
                  setResetForm({
                    ...resetForm,
                    confirmPassword: e.target.value,
                  })
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !resettingPassword) {
                    resetPw();
                  }
                }}
              />

              {resetForm.confirmPassword &&
                resetForm.password !== resetForm.confirmPassword && (
                  <p className="text-xs text-rose-400">
                    Passwords do not match.
                  </p>
                )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeResetPassword}
              disabled={resettingPassword}
            >
              Cancel
            </Button>

            <Button
              data-testid="reset-user-submit"
              onClick={resetPw}
              disabled={
                resettingPassword ||
                !resetForm.password ||
                !resetForm.confirmPassword ||
                resetForm.password.length < 8 ||
                resetForm.password !== resetForm.confirmPassword
              }
              className="bg-[#d4af37] text-black hover:bg-[#e5c158]"
            >
              {resettingPassword ? "Updating..." : "Reset password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        title={confirmDelete ? `Delete ${confirmDelete.name}?` : "Delete user?"}
        description="This is a soft delete — the user is hidden but their history is preserved. Users with open tickets cannot be deleted."
        confirmLabel="Delete"
        destructive
        onConfirm={() => confirmDelete && doDelete(confirmDelete.id)}
        testid="confirm-delete-user"
      />
    </div>
  );
}
